import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.app.yib.io/api/v1';

interface LoginResponse {
  results: {
    token: string;
    refreshToken: string;
    user: {
      id: string;
      email: string;
      username: string;
      firstName?: string;
      lastName?: string;
      data?: {
        yobi_tenant_id: string;
        yobi_user_id: string;
      };
      [key: string]: any;
    };
    [key: string]: any;
  };
  cookies?: {
    access_token: string;
    refresh_token: string;
  };
  status: number;
  success: boolean;
}

interface LoginCredentials {
  user: string;
  password: string;
}

export const authService = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      const payload = {
        user: credentials.user,
        password: credentials.password
      };
      
      const response = await axios.post(`${API_BASE_URL}/users/login`, payload);
      const data = response.data;
      
      if (!data.success || !data.results?.token) {
        throw new Error('Authentication failed');
      }
      
      // Store the token in localStorage
      localStorage.setItem('auth_token', data.results.token);
      if (data.results.refreshToken) {
        localStorage.setItem('refresh_token', data.results.refreshToken);
      }
      
      return data;
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Authentication failed. Please check your credentials.');
    }
  },
  
  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  },
  
  isAuthenticated: (): boolean => {
    // Check URL parameters for tokens
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlRefreshToken = params.get('refreshToken');
    
    // If tokens exist in URL, store them and remove from URL
    if (urlToken) {
      authService.setToken(urlToken, urlRefreshToken || undefined);
      // Clean up URL while preserving other parameters
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.delete('token');
      currentParams.delete('refreshToken');
      const newSearch = currentParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
      return true;
    }
    
    // Otherwise check localStorage
    return !!localStorage.getItem('auth_token');
  },
  
  getToken: (): string | null => {
    return localStorage.getItem('auth_token');
  },
  
  getRefreshToken: (): string | null => {
    return localStorage.getItem('refresh_token');
  },
  
  refreshToken: async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        console.log('No refresh token found in localStorage');
        return false;
      }
      
      // Ensure we're sending the exact specified format
      const payload = { refreshToken };
      console.log('Attempting to refresh token with payload:', payload);
      
      // Make sure the endpoint is correct
      const response = await axios.post(`${API_BASE_URL}/refresh-token`, payload);
      
      console.log('Refresh token response:', response.data);
      const data = response.data;
      
      // Handle the direct response format {token: "...", refreshToken: "..."}
      if (data.token && data.refreshToken) {
        console.log('Token refresh successful - direct format');
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('refresh_token', data.refreshToken);
        return true;
      }
      
      // Handle the nested response format {results: {token: "...", refreshToken: "..."}}
      if (data.results?.token) {
        console.log('Token refresh successful - nested format');
        localStorage.setItem('auth_token', data.results.token);
        if (data.results.refreshToken) {
          localStorage.setItem('refresh_token', data.results.refreshToken);
        }
        return true;
      }
      
      console.log('Token refresh failed - unexpected response format:', data);
      return false;
    } catch (error: any) {
      console.error('Token refresh error:', error);
      
      // Log detailed error information for debugging
      if (error.response) {
        console.error('Refresh token error response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      
      return false;
    }
  },

  setToken: (token: string, refreshToken?: string) => {
    localStorage.setItem('auth_token', token);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
  }
}; 